/**
 * 🏆 PrecisionLeaderboard — top utilisateurs par % FatSecret sur 30 jours
 *
 * Yuri spec 2026-02: gamifie la stat de confiance. Pousse vers les produits
 * québécois connus (qui matchent mieux la DB FatSecret).
 *
 * - Cyan/blue glow signature (cohérent avec FatSecretBadge + TrustStat)
 * - Top 3 podium + 7 suivants en liste compacte
 * - Si l'utilisateur n'est pas dans le top → entrée pinned en bas
 * - Hide gracieusement si l'API échoue ou < 3 users éligibles
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Crown, Medal, Award, Loader2 } from 'lucide-react';
import { api, useLanguage } from '../context/AppContext';
import { Card } from './ui/card';

const RANK_TROPHIES = {
  1: { icon: Crown, color: '#fbbf24', glow: 'rgba(251,191,36,0.7)' },
  2: { icon: Medal, color: '#cbd5e1', glow: 'rgba(203,213,225,0.6)' },
  3: { icon: Award, color: '#fb923c', glow: 'rgba(251,146,60,0.6)' },
};

function Avatar({ url, name, size = 36 }) {
  if (url) {
    return <img src={url} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  const initial = (name || '?').charAt(0).toUpperCase();
  return (
    <div
      className="rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/30 ring-1 ring-cyan-400/40 flex items-center justify-center text-cyan-100 font-bold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initial}
    </div>
  );
}

function LeaderboardRow({ entry, isPodium }) {
  const trophy = RANK_TROPHIES[entry.rank];
  const TrophyIcon = trophy?.icon;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
        entry.is_me
          ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/15 ring-1 ring-cyan-400/60'
          : isPodium
            ? 'bg-zinc-900/60 ring-1 ring-cyan-400/15'
            : 'hover:bg-zinc-900/50'
      }`}
      style={entry.is_me ? { boxShadow: '0 0 12px rgba(34,211,238,0.45)' } : undefined}
      data-testid={`precision-row-${entry.rank}`}
    >
      {/* Rank or trophy */}
      <div className="shrink-0 w-7 flex items-center justify-center">
        {TrophyIcon ? (
          <TrophyIcon className="w-5 h-5" style={{ color: trophy.color, filter: `drop-shadow(0 0 5px ${trophy.glow})` }} />
        ) : (
          <span className="text-xs font-black text-muted-foreground tabular-nums">#{entry.rank}</span>
        )}
      </div>

      {/* Avatar + name */}
      <Avatar url={entry.avatar_url} name={entry.name} size={isPodium ? 38 : 32} />
      <div className="flex-1 min-w-0">
        <p className={`font-heading uppercase tracking-tight text-sm leading-none ${entry.is_me ? 'text-cyan-100' : 'text-foreground'} truncate`}>
          {entry.name}
          {entry.is_me && (
            <span className="ml-2 text-[9px] font-black tracking-widest text-cyan-300 align-middle">· TOI</span>
          )}
        </p>
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5 leading-none">
          {entry.verified_meals}/{entry.total_meals} repas
        </p>
      </div>

      {/* % gauge */}
      <div className="shrink-0 flex items-center gap-1.5">
        <ShieldCheck className="w-3 h-3 text-cyan-300" />
        <span
          className={`font-black text-lg tabular-nums ${entry.is_me ? 'text-cyan-100' : 'text-cyan-200'}`}
          style={{ textShadow: '0 0 8px rgba(34,211,238,0.5)' }}
        >
          {entry.verified_pct}%
        </span>
      </div>
    </div>
  );
}

export default function PrecisionLeaderboard() {
  const { language } = useLanguage();
  const fr = (language || 'fr').startsWith('fr');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get('/leaderboard/precision?limit=10')
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <Card className="bg-card/50 border-border p-6">
        <div className="flex justify-center py-2">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
        </div>
      </Card>
    );
  }

  // Hide if too few eligible users (< 3) — leaderboard would feel empty
  if (!data || (data.total_eligible_users || 0) < 3) return null;

  const top = data.leaderboard || [];
  const me = data.me;
  const meInTop = me && top.some((e) => e.user_id === me.user_id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl border border-cyan-400/40 bg-gradient-to-br from-cyan-950/30 via-zinc-950/85 to-blue-950/25 p-5 overflow-hidden"
      style={{
        boxShadow: '0 0 18px rgba(34,211,238,0.42), 0 0 6px rgba(59,130,246,0.35), inset 0 0 0 1px rgba(34,211,238,0.12)',
      }}
      data-testid="precision-leaderboard"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div
          className="shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 ring-1 ring-cyan-400/60 flex items-center justify-center"
          style={{ boxShadow: '0 0 14px rgba(34,211,238,0.65)' }}
        >
          <ShieldCheck className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <p className="text-[9px] uppercase tracking-[0.28em] font-bold text-cyan-300">
              {fr ? 'Top Précision' : 'Top Precision'}
            </p>
            <span className="text-[10px] leading-none">🇨🇦</span>
          </div>
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground leading-tight">
            {fr ? 'Macros vérifiées · 30 jours' : 'Verified macros · 30 days'}
          </h3>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {fr
              ? `${data.total_eligible_users} athlètes éligibles (min. 5 repas)`
              : `${data.total_eligible_users} eligible athletes (min 5 meals)`}
          </p>
        </div>
      </div>

      {/* Rows */}
      <div className="space-y-1">
        {top.map((entry, idx) => (
          <LeaderboardRow key={entry.user_id} entry={entry} isPodium={idx < 3} />
        ))}
      </div>

      {/* User entry pinned at bottom if not in top */}
      {me && !meInTop && (
        <>
          <div className="flex items-center gap-2 mt-3 mb-1.5">
            <div className="flex-1 h-px bg-cyan-400/20" />
            <span className="text-[9px] uppercase tracking-widest font-bold text-cyan-400/70">
              {fr ? 'Ton classement' : 'Your rank'}
            </span>
            <div className="flex-1 h-px bg-cyan-400/20" />
          </div>
          <LeaderboardRow entry={me} isPodium={false} />
        </>
      )}

      {!me && (
        <p className="text-xs text-muted-foreground italic text-center mt-3 leading-relaxed">
          {fr
            ? 'Tracke 5 repas cette semaine pour entrer au classement.'
            : 'Track 5 meals this week to enter the leaderboard.'}
        </p>
      )}
    </motion.div>
  );
}
